import React, { useState, useEffect, useRef } from "react";
import { makeStyles } from "@material-ui/core/styles";
import { 
  Typography, 
  Paper,
  Button
} from "@material-ui/core";
import AddIcon from "@material-ui/icons/Add";
import SaveIcon from "@material-ui/icons/Save";
import FileCopyIcon from "@material-ui/icons/FileCopy";
import RefreshIcon from "@material-ui/icons/Refresh";
import ModalWrapper from "@View/components/ModalWrapper";

// Импорты созданных модулей
import {
  useAccountEditorAPI,
  useIframeMessaging,
  useSearchHandler,
  useAccountsState,
  DatePickerDialog,
  SearchField,
  formatDateToDisplay,
  collectDatesFromAccount,
  formatDateForDisplay,
  IFRAME_ACTIONS,
  REFRESH_DELAY_MS
} from "./AccountEditor";

const useStyles = makeStyles((theme) => ({
  modalContent: {
    height: "calc(100vh - 120px)",
    display: "flex",
    flexDirection: "column",
    padding: 0,
  },
  iframe: {
    border: 'none',
    width: '100%',
    flex: 1,
    height: '100%',
  },
  headerPaper: {
    padding: theme.spacing(1.5),
    marginBottom: theme.spacing(1.5),
    borderRadius: "6px",
    border: "1px solid #E1E6F1",
    backgroundColor: "#ffffff",
    flexShrink: 0,
  },
  actionButtons: {
    display: "flex",
    gap: theme.spacing(1),
    flexWrap: "wrap",
    marginBottom: theme.spacing(1),
    "& .MuiButton-root": {
      borderRadius: "5px",
      border: "1px solid #E1E6F1",
      backgroundColor: "#F3F5F7",
      color: "#1A1E24",
      fontWeight: 500,
      fontSize: "12px",
      textTransform: "none",
      padding: theme.spacing(0.75, 1.5),
      minHeight: "32px",
      "&:hover": {
        backgroundColor: "#E1E6F1",
        borderColor: "#D1D6E1",
      },
    },
    "& .save-button": {
      marginRight: theme.spacing(1.5),
      position: "relative",
      "&::after": {
        content: '""',
        position: "absolute",
        right: "-14px",
        top: "50%",
        transform: "translateY(-50%)",
        width: "1px",
        height: "20px",
        backgroundColor: "#E1E6F1",
      },
    },
    "& .add-account-button": {
      marginLeft: theme.spacing(0.5),
    },
  },
  headerContainer: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    gap: theme.spacing(2),
    marginBottom: theme.spacing(1),
  },
  buttonsContainer: {
    flex: 1,
  },
  searchContainer: {
    minWidth: "350px",
    maxWidth: "400px",
    alignSelf: "flex-start",
  },
  customSearchField: {
    display: "flex",
    alignItems: "center",
    height: "36px",
    border: "1px solid #E1E6F1",
    borderRadius: "6px",
    backgroundColor: "#ffffff",
    paddingLeft: "12px",
    paddingRight: "14px",
    fontSize: "13px",
    position: "relative",
    overflow: "hidden",
    "&:hover": {
      borderColor: "#D1D6E1",
    },
    "&:focus-within": {
      borderColor: "#007bff",
      outline: "none",
    },
    "& input": {
      border: "none",
      outline: "none",
      background: "transparent",
      fontSize: "13px",
      flex: 1,
      marginLeft: "8px",
      position: "relative",
      zIndex: 1,
      "&::placeholder": {
        color: "#54606F",
      },
    },
    "& .MuiSvgIcon-root": {
      position: "relative",
      zIndex: 1,
    },
  },
  dialogPaper: {
    borderRadius: "8px",
    border: "1px solid #E1E6F1",
  },
  dialogTitle: {
    borderBottom: "1px solid #E1E6F1",
    padding: theme.spacing(2, 3),
    "& .MuiTypography-h6": {
      fontSize: "16px",
      fontWeight: 600,
      color: "#1A1E24",
    },
  },
  dialogContent: {
    padding: theme.spacing(3),
  },
  dialogActions: {
    borderTop: "1px solid #E1E6F1",
    padding: theme.spacing(2, 3),
    gap: theme.spacing(1),
    "& .MuiButton-root": {
      borderRadius: "5px",
      border: "1px solid #E1E6F1",
      backgroundColor: "#F3F5F7",
      color: "#1A1E24",
      fontWeight: 500,
      fontSize: "12px",
      textTransform: "none",
      padding: theme.spacing(0.75, 1.5),
      minHeight: "32px",
      "&:hover": {
        backgroundColor: "#E1E6F1",
        borderColor: "#D1D6E1",
      },
    },
    "& .cancel-button": {
      backgroundColor: "#ffffff",
      color: "#54606F",
      "&:hover": {
        backgroundColor: "#F8F9FA",
        borderColor: "#D1D6E1",
      },
    },
  },
  formControl: {
    "& .MuiOutlinedInput-root": {
      borderRadius: "6px",
      backgroundColor: "#ffffff",
      fontSize: "14px",
      "& fieldset": {
        borderColor: "#E1E6F1",
      },
      "&:hover fieldset": {
        borderColor: "#D1D6E1",
      },
      "&.Mui-focused fieldset": {
        borderColor: "#007bff",
      },
    },
    "& .MuiInputLabel-outlined": {
      fontSize: "14px",
      color: "#54606F",
      "&.Mui-focused": {
        color: "#007bff",
      },
    },
  },
  previewText: {
    fontSize: "13px",
    color: "#54606F",
    backgroundColor: "#F3F5F7",
    padding: theme.spacing(1.5),
    borderRadius: "6px",
    border: "1px solid #E1E6F1",
  },
  iframePaper: {
    padding: 0,
    borderRadius: "8px",
    border: "1px solid #E1E6F1",
    overflow: "hidden",
    flex: 1,
  },
  controlsSection: {
    paddingTop: theme.spacing(0),
  },
  segmentedControl: {
    display: "flex",
    backgroundColor: "#F3F5F7",
    border: "1px solid #E1E6F1",
    borderRadius: "6px",
    padding: "3px",
    gap: "1px",
    marginTop: theme.spacing(0.5),
    flexWrap: "wrap",
    maxHeight: "80px",
    overflowY: "auto",
    "&::-webkit-scrollbar": {
      width: "4px",
    },
    "&::-webkit-scrollbar-track": {
      backgroundColor: "#f1f1f1",
      borderRadius: "2px",
    },
    "&::-webkit-scrollbar-thumb": {
      backgroundColor: "#c1c1c1",
      borderRadius: "2px",
    },
  },
  segmentButton: {
    backgroundColor: "transparent",
    border: "none",
    borderRadius: "4px",
    padding: theme.spacing(0.75, 1.5),
    fontSize: "12px",
    fontWeight: 500,
    color: "#54606F",
    cursor: "pointer",
    transition: "all 0.2s ease",
    whiteSpace: "nowrap",
    minWidth: "fit-content",
    "&:hover": {
      backgroundColor: "#E1E6F1",
      color: "#1A1E24",
    },
    "&.active": {
      backgroundColor: "#ffffff",
      color: "#1A1E24",
      fontWeight: 600,
      boxShadow: "0 1px 2px rgba(0, 0, 0, 0.1)",
    },
  },
}));

const HtmlAccountEditorModal = ({ open, onClose, requestId }) => {
  const classes = useStyles();
  const iframeRef = useRef(null);

  // Состояние для деталей запроса и диалога выбора даты
  const [requestDetails, setRequestDetails] = useState(null);
  const [datePickerOpen, setDatePickerOpen] = useState(false);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth() + 1);

  // Используем кастомные хуки
  const { loading, fetchRequestDetails, fetchAccounts, saveAccounts } = useAccountEditorAPI();
  const {
    accounts,
    selectedAccountId,
    setSelectedAccountId,
    accountsData,
    setAccountsData,
    availableDates,
    updateAccountsData,
    addAccount,
    deleteAccount,
    addSubAccount,
    deleteSubAccount,
    addCounterparty,
    deleteCounterparty,
    deleteDate,
    addDate,
    getSelectedAccount
  } = useAccountsState();

  // Обработчики сообщений от iframe
  const messageHandlers = {
    [IFRAME_ACTIONS.ACCOUNT_DATA_UPDATED]: () => {
      console.log('Account data updated in iframe');
    },
    [IFRAME_ACTIONS.DELETE_DATE]: (payload) => handleDeleteDate(payload),
    [IFRAME_ACTIONS.DELETE_SUBACCOUNT]: (payload) => handleDeleteSubAccount(payload.subIndex),
    [IFRAME_ACTIONS.ADD_COUNTERPARTY]: (payload) => handleAddCounterparty(payload.subIndex),
    [IFRAME_ACTIONS.DELETE_COUNTERPARTY]: (payload) => handleDeleteCounterparty(payload.subIndex, payload.cpIndex),
    [IFRAME_ACTIONS.ADD_SUBACCOUNT]: () => handleAddSubAccount(),
    [IFRAME_ACTIONS.DELETE_ACCOUNT]: () => handleDeleteAccount()
  };

  // Инициализация хуков для поиска и messaging
  const { searchTerm, searchProgress, handleSearchChange, setSearchTerm } = useSearchHandler(
    (term) => searchInIframe(term)
  );

  const { 
    displayDataInIframe, 
    searchInIframe, 
    getAccountDataFromIframe 
  } = useIframeMessaging(iframeRef, messageHandlers);

  // Загрузка данных при открытии модалки
  useEffect(() => {
    if (open && requestId) {
      handleLoadData();
    }
  }, [open, requestId]);

  const handleLoadData = async () => {
    try {
      // Загружаем детали запроса и счета параллельно
      const [details, accountsArray] = await Promise.all([
        fetchRequestDetails(requestId),
        fetchAccounts(requestId)
      ]);
      
      if (details) {
        setRequestDetails(details);
      }
      
      if (accountsArray.length > 0) {
        const normalizedAccounts = accountsArray.map((account, index) => ({
          ...account,
          title: account.title || `Account ${index}`,
          values: (account.values || []).map(subAccount => ({
            ...subAccount,
            title: subAccount.title || 'Sub-account',
            values: (subAccount.values || []).map(contractor => ({ 
              ...contractor, 
              title: contractor.title || 'Contractor' 
            }))
          }))
        }));
        updateAccountsData(normalizedAccounts);
      } else {
        alert("В этом запросе нет загруженных счетов.");
      }
    } catch (error) {
      console.error("Error loading data:", error);
      alert("Произошла ошибка при получении данных.");
    }
  };

  // Отправка данных в iframe при выборе счета
  useEffect(() => {
    if (selectedAccountId && accountsData.length > 0) {
      const selectedAccount = getSelectedAccount();
      if (selectedAccount) {
        const sortedDates = collectDatesFromAccount(selectedAccount);
        displayDataInIframe(selectedAccount, sortedDates, searchTerm);
      }
    }
  }, [selectedAccountId, accountsData, searchTerm, displayDataInIframe, getSelectedAccount]);

  // Применение поиска при переключении счетов
  useEffect(() => {
    if (selectedAccountId && searchTerm.trim() !== '') {
      searchInIframe(searchTerm);
    }
  }, [selectedAccountId, searchTerm, searchInIframe]);

  // Обработчики действий
  const handleRefreshAccounts = () => {
    handleLoadData();
  };

  const handleAddAccount = () => {
    const accountTitle = window.prompt("Введите название нового счета:");
    if (accountTitle && accountTitle.trim()) {
      addAccount(accountTitle.trim());
    }
  };

  const handleCopyAccount = () => {
    if (!selectedAccountId) {
      alert("Пожалуйста, выберите счет для копирования.");
      return;
    }
    
    const accountToCopy = getSelectedAccount();
    if (!accountToCopy) {
      alert("Выбранный счет не найден.");
      return;
    }

    const accountTitle = window.prompt("Введите название для копии счета:", `${accountToCopy.title} (копия)`);
    if (accountTitle && accountTitle.trim()) {
      addAccount(accountTitle.trim(), accountToCopy);
    }
  };

  const handleDeleteAccount = () => {
    if (!selectedAccountId) return;
    if (!window.confirm("Вы уверены, что хотите удалить выбранный счет?")) return;
    deleteAccount(selectedAccountId);
  };

  const handleDeleteDate = (dateToDelete) => {
    const formattedDate = formatDateForDisplay(dateToDelete);
    if (!window.confirm(`Вы уверены, что хотите удалить дату ${formattedDate}?`)) return;
    deleteDate(dateToDelete);
  };

  const handleDeleteSubAccount = (subIndex) => {
    if (!window.confirm("Вы уверены, что хотите удалить этот субсчет?")) return;
    deleteSubAccount(subIndex);
  };

  const handleAddCounterparty = (subIndex) => {
    const counterpartyTitle = window.prompt("Введите название контрагента:");
    if (counterpartyTitle && counterpartyTitle.trim()) {
      addCounterparty(subIndex, counterpartyTitle.trim());
    }
  };

  const handleDeleteCounterparty = (subIndex, cpIndex) => {
    if (!window.confirm("Вы уверены, что хотите удалить этого контрагента?")) return;
    deleteCounterparty(subIndex, cpIndex);
  };

  const handleAddDate = () => {
    setDatePickerOpen(true);
  };

  const handleDatePickerConfirm = () => {
    const lastDayOfMonth = new Date(selectedYear, selectedMonth, 0).getDate();
    const newDate = `${selectedYear}-${selectedMonth.toString().padStart(2, '0')}-${lastDayOfMonth.toString().padStart(2, '0')}`;
    
    if (availableDates.includes(newDate)) {
      alert("Эта дата уже существует.");
      setDatePickerOpen(false);
      return;
    }

    addDate(newDate);
    setDatePickerOpen(false);
  };

  const handleAddSubAccount = () => {
    if (!selectedAccountId) {
      alert("Пожалуйста, выберите счет сначала.");
      return;
    }
    const subAccountTitle = window.prompt("Введите название субсчета:");
    if (subAccountTitle && subAccountTitle.trim()) {
      addSubAccount(subAccountTitle.trim());
    }
  };

  const handleSaveAccounts = async () => {
    if (accountsData.length === 0) {
      alert("Нет данных для сохранения.");
      return;
    }
    
    try {
      const updatedAccountData = await getAccountDataFromIframe();
      const updatedAccountsData = accountsData.map(account => 
        account.title === selectedAccountId ? updatedAccountData : account
      );

      await saveAccounts(requestId, updatedAccountsData);
      setAccountsData(updatedAccountsData);
      
      // Обновляем данные после сохранения
      setTimeout(() => {
        handleLoadData();
      }, REFRESH_DELAY_MS);
    } catch (error) {
      console.error("Error saving accounts:", error);
      const errorMessage = error?.message || error?.toString() || "Неизвестная ошибка";
      alert("Ошибка при сохранении изменений: " + errorMessage);
    }
  };

  return (
    <ModalWrapper
      open={open}
      handleClose={onClose}
      maxWidth="95vw"
      maxHeight="95vh"
    >
      <div className={classes.modalContent}>
        
        <Typography variant="h6" gutterBottom style={{ marginBottom: 16, flexShrink: 0 }}>
          {requestDetails ? 
            `Редактор счетов клиента '${requestDetails.clientName}'. Запрос ${formatDateToDisplay(requestDetails.startDate)} - ${formatDateToDisplay(requestDetails.endDate)}` : 
            `Редактор счетов - Запрос #${requestId}`
          }
        </Typography>
        
        {/* Секция управления */}
        <Paper className={classes.headerPaper}>
          <div className={classes.headerContainer}>
            <div className={classes.buttonsContainer}>
              <div className={classes.actionButtons}>
                <Button
                  onClick={handleRefreshAccounts}
                  disabled={loading}
                  startIcon={<RefreshIcon />}
                  className="refresh-button"
                >
                  {loading ? "Загрузка..." : "Обновить"}
                </Button>

                <Button
                  onClick={handleSaveAccounts}
                  disabled={loading || accountsData.length === 0}
                  startIcon={<SaveIcon />}
                  className="save-button"
                >
                  Сохранить
                </Button>

                <Button
                  startIcon={<AddIcon />}
                  onClick={handleAddAccount}
                  disabled={loading}
                  className="add-account-button"
                >
                  Добавить счет
                </Button>

                <Button
                  startIcon={<AddIcon />}
                  onClick={handleAddDate}
                  disabled={accounts.length === 0}
                >
                  Добавить отчетный период
                </Button>

                <Button
                  startIcon={<FileCopyIcon />}
                  onClick={handleCopyAccount}
                  disabled={!selectedAccountId || loading}
                >
                  Копировать счет
                </Button>
              </div>
            </div>
            
            <SearchField
              searchTerm={searchTerm}
              onSearchChange={handleSearchChange}
              searchProgress={searchProgress}
              classes={classes}
            />
          </div>
          
          {/* Segmented Control для выбора счетов */}
          {accounts.length > 0 && (
            <div className={classes.controlsSection}>              
              <div className={classes.segmentedControl}>
                {accounts.map((account) => (
                  <button
                    key={account.title}
                    className={`${classes.segmentButton} ${selectedAccountId === account.title ? 'active' : ''}`}
                    onClick={() => setSelectedAccountId(account.title)}
                  >
                    {account.title}
                  </button>
                ))}
              </div>
            </div>
          )}
        </Paper>
        
        {/* Dialog для добавления даты */}
        <DatePickerDialog
          open={datePickerOpen}
          onClose={() => setDatePickerOpen(false)}
          selectedYear={selectedYear}
          setSelectedYear={setSelectedYear}
          selectedMonth={selectedMonth}
          setSelectedMonth={setSelectedMonth}
          onConfirm={handleDatePickerConfirm}
          classes={classes}
        />

        {/* Iframe */}
        <Paper className={classes.iframePaper}>
          <iframe
            ref={iframeRef}
            className={classes.iframe}
            src="/accountEditor.html"
            title="HTML Account Editor"
            onLoad={() => {
              if (selectedAccountId && accountsData.length > 0) {
                const selectedAccount = getSelectedAccount();
                if (selectedAccount) {
                  const sortedDates = collectDatesFromAccount(selectedAccount);
                  displayDataInIframe(selectedAccount, sortedDates, searchTerm);
                }
              }
            }}
          />
        </Paper>
      </div>
    </ModalWrapper>
  );
};

export default HtmlAccountEditorModal;